import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { AdminLayout } from '@/components/AdminLayout';
import { Plus, Shield, Edit2, Trash2, Mail, User, Key, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    name: ''
  });
  
  const [editForm, setEditForm] = useState({
    email: '',
    name: '',
    password: ''
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const res = await axios.get(`${API}/admin/super-admins`);
      setAdmins(res.data);
    } catch (e) {
      toast.error('Erro ao carregar administradores');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.email || !createForm.password) {
      toast.error('Email e senha são obrigatórios');
      return;
    }

    try {
      await axios.post(`${API}/admin/super-admins`, createForm);
      toast.success('Administrador criado com sucesso!');
      setShowCreateModal(false);
      setCreateForm({ email: '', password: '', name: '' });
      fetchAdmins();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar administrador');
    }
  };

  const openEditModal = (admin) => {
    setSelectedAdmin(admin);
    setEditForm({
      email: admin.email,
      name: admin.name || '',
      password: ''
    });
    setShowEditModal(true);
  };

  const handleEdit = async () => {
    if (!editForm.email) {
      toast.error('Email é obrigatório');
      return;
    }

    try {
      const updateData = {
        email: editForm.email,
        name: editForm.name
      };
      if (editForm.password) {
        updateData.password = editForm.password;
      }

      await axios.put(`${API}/admin/super-admins/${selectedAdmin.id}`, updateData);
      toast.success('Administrador atualizado com sucesso!');
      setShowEditModal(false);
      fetchAdmins();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar administrador');
    }
  };

  const handleDelete = async (admin) => {
    if (admin.id === currentUser?.id) {
      toast.error('Você não pode excluir sua própria conta');
      return;
    }

    if (!confirm(`Tem certeza que deseja excluir o administrador "${admin.email}"?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/super-admins/${admin.id}`);
      toast.success('Administrador excluído com sucesso!');
      fetchAdmins();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir administrador');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Administradores">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ff6b00]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Administradores">
      <div className="space-y-6">
        {/* Header */}
        <Card className="card-adegga border-purple-500/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Super Administradores</h2>
                  <p className="text-white/60">
                    Gerencie os usuários com acesso total ao sistema
                  </p>
                </div>
              </div>
              <Button onClick={() => setShowCreateModal(true)} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-2" />
                Novo Admin
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Warning */}
        <div className="flex items-center gap-3 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-yellow-500" />
          <p className="text-yellow-200 text-sm">
            Super Administradores têm acesso total ao sistema, incluindo dados de todas as lojas. 
            Adicione apenas pessoas de confiança.
          </p>
        </div>

        {/* Admins List */}
        <Card className="card-adegga">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead className="text-white/60">Administrador</TableHead>
                <TableHead className="text-white/60">Email</TableHead>
                <TableHead className="text-white/60">Criado em</TableHead>
                <TableHead className="text-white/60 text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.map(admin => (
                <TableRow key={admin.id} className="border-white/5">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <span className="text-white font-medium">{admin.name || 'Sem nome'}</span>
                        {admin.id === currentUser?.id && (
                          <Badge className="ml-2 bg-[#ff6b00]/20 text-[#ff6b00] text-xs">Você</Badge>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-white/60">{admin.email}</TableCell>
                  <TableCell className="text-white/60">
                    {admin.created_at ? new Date(admin.created_at).toLocaleDateString('pt-BR') : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-blue-400"
                        onClick={() => openEditModal(admin)}
                        title="Editar"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      {admin.id !== currentUser?.id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400"
                          onClick={() => handleDelete(admin)}
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {admins.length === 0 && (
            <div className="text-center py-12 text-white/40">
              Nenhum administrador cadastrado
            </div>
          )}
        </Card>

        {/* Create Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="bg-[#13131f] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-400" />
                Novo Super Administrador
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-white/60 mb-1 block">Nome</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    placeholder="Nome do administrador"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    className="bg-[#0f0f15] border-white/10 text-white pl-10"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-white/60 mb-1 block">Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    type="email"
                    placeholder="email@exemplo.com"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    className="bg-[#0f0f15] border-white/10 text-white pl-10"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-white/60 mb-1 block">Senha *</label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    type="password"
                    placeholder="Senha de acesso"
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    className="bg-[#0f0f15] border-white/10 text-white pl-10"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowCreateModal(false)}>Cancelar</Button>
              <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleCreate}>
                Criar Administrador
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="bg-[#13131f] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-blue-400" />
                Editar Administrador
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-white/60 mb-1 block">Nome</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    placeholder="Nome do administrador"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="bg-[#0f0f15] border-white/10 text-white pl-10"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-white/60 mb-1 block">Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    type="email"
                    placeholder="email@exemplo.com"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="bg-[#0f0f15] border-white/10 text-white pl-10"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-white/60 mb-1 block">Nova Senha</label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    type="password"
                    placeholder="Deixe vazio para manter a atual"
                    value={editForm.password}
                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                    className="bg-[#0f0f15] border-white/10 text-white pl-10"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowEditModal(false)}>Cancelar</Button>
              <Button className="bg-[#ff6b00] hover:bg-[#ff8533]" onClick={handleEdit}>
                Salvar Alterações
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
